import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// Helper to format yyyy-mm-dd to mm/dd/yyyy
function formatDateToMMDDYYYY(dateStr) {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  if (!year || !month || !day) return dateStr;
  return `${month}/${day}/${year}`;
}
// Helper to parse mm/dd/yyyy to yyyy-mm-dd
function parseDateToYYYYMMDD(dateStr) {
  if (!dateStr) return '';
  const [month, day, year] = dateStr.split('/');
  if (!year || !month || !day) return dateStr;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

const calendarPopper = ({ children }) => (
  typeof document === 'undefined' ? children : createPortal(children, document.body)
);

const MappingsCreateReceiving = ({ onCreate, showButton = true, loading = false, comments: initialComments = '', shipmentDate: initialShipmentDate = '', shipmentNumber }) => {
  
  const [comments, setComments] = useState(initialComments);
  // Convert initialShipmentDate (MM/DD/YYYY) to Date object
  const parseInitialDate = (dateStr) => {
    if (!dateStr) return null;
    
    // Handle MM/DD/YYYY or MM/DD/YYYY hh:mm AM/PM (from API response)
    if (dateStr.includes('/')) {
      // Expected formats:
      // - 04/29/2026
      // - 04/29/2026 04:55 PM
      const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?$/i);
      if (!match) return null;
      const [, mm, dd, yyyy, hhRaw, minRaw, ampmRaw] = match;
      const month = mm.padStart(2, '0');
      const day = dd.padStart(2, '0');
      const year = yyyy;
      let hours = hhRaw ? parseInt(hhRaw, 10) : 0;
      const minutes = minRaw ? parseInt(minRaw, 10) : 0;
      const ampm = ampmRaw ? ampmRaw.toUpperCase() : null;
      if (ampm) {
        if (ampm === 'PM' && hours < 12) hours += 12;
        if (ampm === 'AM' && hours === 12) hours = 0;
      }
      const dateObj = new Date(`${year}-${month}-${day}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`);
      return isNaN(dateObj) ? null : dateObj;
    }
    
    // Handle YYYY-MM-DD format (fallback)
    if (dateStr.includes('-')) {
      // Supports:
      // - 2026-05-06
      // - 2026-05-06T16:45
      // - 2026-05-06T16:45:00
      const d = new Date(dateStr);
      return isNaN(d) ? null : d;
    }
    
    return null;
  };
  const [date, setDate] = useState(parseInitialDate(initialShipmentDate));

  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  useEffect(() => {
    const parsedDate = parseInitialDate(initialShipmentDate);
    setDate(parsedDate);
  }, [initialShipmentDate]);

  const handleCreate = () => {
    if (onCreate) {
      let formattedDate = '';
      if (date instanceof Date && !isNaN(date)) {
        // Send ISO local datetime (no timezone suffix) so API can store time accurately.
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const ss = String(date.getSeconds()).padStart(2, '0');
        formattedDate = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
      }
      onCreate({
        comments,
        shipmentDate: formattedDate
      });
    }
  };

  return (
    <div className="row mb-4 align-items-center">
      {shipmentNumber && (
        <div className="col-12 mb-2">
          <div className="alert alert-success p-2 mb-2">
            <strong>Shipment Number:</strong> {shipmentNumber}
          </div>
        </div>
      )}
      <div className="col-md-6 mb-2 mb-md-0">
        <label className="form-label"><strong>Comments</strong></label>
        <textarea
          className="form-control"
          value={comments}
          onChange={e => setComments(e.target.value)}
          placeholder="Enter comments"
          rows={2}
          disabled={!showButton}
        />
      </div>
      <div className="col-md-3 mb-2 mb-md-0">
        <label className="form-label"><strong>Date &amp; Time</strong></label>
        <DatePicker
          selected={date}
          onChange={date => setDate(date)}
          showTimeSelect
          timeIntervals={5}
          timeCaption="Time"
          dateFormat="MM/dd/yyyy h:mm aa"
          className="form-control"
          placeholderText="MM/DD/YYYY hh:mm AM/PM"
          disabled={!showButton}
          autoComplete="off"
          popperContainer={calendarPopper}
          popperClassName="receiving-datepicker-popper"
        />
      </div>
      {showButton && (
        <div className="col-md-3 d-flex flex-column justify-content-end mb-2 mb-md-0" style={{height: '100%'}}>
          <label className="form-label" style={{ visibility: 'hidden' }}>&nbsp;</label>
          <button
            className="btn btn-primary w-100 mt-auto"
            type="button"
            onClick={handleCreate}
            disabled={loading}
            style={{ minHeight: 48 }}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Creating...
              </>
            ) : (
              'Create Receiving Shipment'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default MappingsCreateReceiving;
